import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { wabaApi } from "../../lib/wabaApi";
import { DialogPanel } from "../shared/DialogPanel";
import { XIcon } from "../shared/Icons";
import { backdropClose } from "../shared/backdrop";

interface Template {
  name: string;
  status: string;    // APPROVED | PENDING | REJECTED
  category: string;  // MARKETING | UTILITY | AUTHENTICATION
  language: string;
  components: any[];
}

interface Props {
  connectionId: string;
  onClose: () => void;
  canCreate?: boolean;
}

interface Alert {
  id: string;
  kind: "template" | "restriction";
  title: string;
  detail: string | null;
  created_at: string;
}

function fmtAlertTime(iso: string) {
  const utc = iso.endsWith("Z") || iso.includes("+") ? iso : iso + "Z";
  return new Date(utc).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit", timeZone: "America/Sao_Paulo" });
}

const STATUS_COLOR: Record<string, string> = {
  APPROVED: "var(--ok)",
  PENDING: "var(--warn)",
  REJECTED: "var(--danger)",
};

const COMP_LABEL: Record<string, string> = {
  HEADER: "Cabeçalho", BODY: "Corpo", FOOTER: "Rodapé", BUTTONS: "Botões",
};

/** Miolo do template: o que a Meta devolve em `components`, em texto simples. */
function TemplateBody({ components }: { components: any[] }) {
  const vars = [...new Set(
    components.flatMap((c) => String(c.text ?? "").match(/\{\{\d+\}\}/g) ?? [])
  )];
  const line: React.CSSProperties = { fontSize: 12, color: "var(--ink-secondary)", whiteSpace: "pre-wrap", wordBreak: "break-word" };
  const tag: React.CSSProperties = { fontSize: 10, fontWeight: 600, color: "var(--ink-muted)", letterSpacing: 0.4 };

  return (
    <div style={{ borderTop: "1px solid var(--line)", padding: "10px 14px 12px", display: "flex", flexDirection: "column", gap: 10 }}>
      {components.length === 0 && <span style={line}>Template sem componentes.</span>}
      {components.map((c, i) => (
        <div key={i} style={{ display: "flex", flexDirection: "column", gap: 3 }}>
          <span style={tag}>{(COMP_LABEL[c.type] ?? c.type ?? "?").toUpperCase()}{c.format && c.format !== "TEXT" ? ` · ${c.format}` : ""}</span>
          {c.text && <span style={line}>{c.text}</span>}
          {Array.isArray(c.buttons) && c.buttons.map((b: any, j: number) => (
            <span key={j} style={{ ...line, color: "var(--ink-muted)" }}>
              • {b.text} <span style={{ fontSize: 10 }}>({b.type}{b.url ? ` → ${b.url}` : ""}{b.phone_number ? ` → ${b.phone_number}` : ""})</span>
            </span>
          ))}
          {!c.text && !c.buttons && <span style={{ ...line, color: "var(--ink-muted)" }}>sem texto — preenchido no envio</span>}
        </div>
      ))}
      {vars.length > 0 && (
        <span style={{ fontSize: 11, color: "var(--ink-muted)" }}>Variáveis: {vars.join(", ")}</span>
      )}
    </div>
  );
}

export function TemplateManager({ connectionId, onClose, canCreate = true }: Props) {
  const qc = useQueryClient();
  const [openKey, setOpenKey] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ name: "", language: "pt_BR", category: "UTILITY", bodyText: "" });
  const [formError, setFormError] = useState<string | null>(null);
  // Exemplos por variável ({{1}}, {{2}}...) — a Meta exige na aprovação
  const [examples, setExamples] = useState<Record<string, string>>({});
  const varNums = [...new Set((form.bodyText.match(/\{\{(\d+)\}\}/g) ?? []).map((m) => m.replace(/\D/g, "")))]
    .sort((a, b) => Number(a) - Number(b));
  const examplesMissing = varNums.some((n) => !examples[n]?.trim());

  const { data: templates = [], isLoading } = useQuery<Template[]>({
    queryKey: ["waba-templates", connectionId],
    queryFn: () => wabaApi.get(`/connections/${connectionId}/templates`),
  });

  // Avisos da Meta (status/qualidade/categoria de template, restrição da conta):
  // somem sozinhos em 72h ou no X (definitivo, para todos).
  const { data: alerts = [] } = useQuery<Alert[]>({
    queryKey: ["waba-template-alerts", connectionId],
    queryFn: () => wabaApi.get(`/connections/${connectionId}/templates/alerts`),
  });
  const dismissMut = useMutation({
    mutationFn: (id: string) => wabaApi.post(`/connections/${connectionId}/templates/alerts/${id}/dismiss`, {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["waba-template-alerts", connectionId] }),
  });

  const createMut = useMutation({
    mutationFn: () =>
      wabaApi.post(`/connections/${connectionId}/templates`, {
        name: form.name,
        language: form.language,
        category: form.category,
        components: [{
          type: "BODY",
          text: form.bodyText,
          ...(varNums.length ? { example: { body_text: [varNums.map((n) => examples[n].trim())] } } : {}),
        }],
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["waba-templates", connectionId] });
      setCreating(false);
      setForm({ name: "", language: "pt_BR", category: "UTILITY", bodyText: "" });
      setExamples({});
      setFormError(null);
    },
    onError: (err: any) => {
      const d = err?.payload?.detail;
      setFormError(typeof d === "string" ? d : d?.error ?? "Erro ao criar template.");
    },
  });

  const deleteMut = useMutation({
    mutationFn: (name: string) => wabaApi.delete(`/connections/${connectionId}/templates/${name}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["waba-templates", connectionId] }),
  });

  const inputStyle: React.CSSProperties = {
    padding: "7px 10px", background: "var(--surface-input)", border: "1px solid var(--line)",
    borderRadius: 6, color: "var(--ink)", fontSize: 13, outline: "none", width: "100%",
  };
  const selectStyle: React.CSSProperties = { ...inputStyle, cursor: "pointer" };

  return (
    <div
      style={{ position: "fixed", inset: 0, background: "var(--overlay)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: "var(--z-modal)" as any }}
      {...backdropClose(() => { onClose(); })}
    >
      <DialogPanel onClose={() => onClose()} label="Templates de Mensagem" style={{ background: "var(--surface-hover)", border: "1px solid var(--line)", borderRadius: 12, padding: 24, width: 560, maxHeight: "80vh", display: "flex", flexDirection: "column", gap: 16 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <span style={{ fontSize: 15, fontWeight: 600, color: "var(--ink)" }}>Templates de Mensagem</span>
          <button aria-label="Fechar" onClick={onClose} style={{ background: "transparent", border: "none", color: "var(--ink-muted)", fontSize: 18, cursor: "pointer", padding: "2px 6px" }}><XIcon size={16} /></button>
        </div>

        {/* Avisos da Meta */}
        {alerts.length > 0 && (
          <div style={{ display: "flex", flexDirection: "column", gap: 6, maxHeight: 180, overflowY: "auto" }}>
            {alerts.map((a) => {
              const danger = a.kind === "restriction";
              return (
                <div key={a.id} style={{ display: "flex", alignItems: "flex-start", gap: 8, padding: "8px 10px", borderRadius: 8, background: danger ? "var(--danger-soft)" : "var(--warn-soft)", border: `1px solid ${danger ? "var(--danger-soft-line)" : "var(--warn-line)"}` }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: danger ? "var(--danger)" : "var(--warn)" }}>{a.title}</div>
                    {a.detail && <div style={{ fontSize: 11, color: "var(--ink-secondary)", marginTop: 2 }}>{a.detail}</div>}
                    <div style={{ fontSize: 10, color: "var(--ink-muted)", marginTop: 2 }}>{fmtAlertTime(a.created_at)}</div>
                  </div>
                  <button aria-label="Fechar aviso" title="Fechar de vez" onClick={() => dismissMut.mutate(a.id)} style={{ background: "transparent", border: "none", color: "var(--ink-muted)", cursor: "pointer", padding: "2px 4px", flexShrink: 0 }}><XIcon size={12} /></button>
                </div>
              );
            })}
          </div>
        )}

        {/* Lista */}
        <div style={{ flex: 1, overflow: "auto", display: "flex", flexDirection: "column", gap: 8 }}>
          {isLoading && <span style={{ fontSize: 13, color: "var(--ink-muted)" }}>Carregando...</span>}
          {templates.map((t) => {
            const key = `${t.name}-${t.language}`;
            const open = openKey === key;
            return (
            <div key={key} style={{ background: "var(--surface-input)", border: "1px solid var(--line)", borderRadius: 8 }}>
              <div style={{ padding: "12px 14px", display: "flex", alignItems: "center", gap: 12 }}>
                <button
                  onClick={() => setOpenKey(open ? null : key)}
                  aria-expanded={open}
                  style={{ flex: 1, minWidth: 0, background: "transparent", border: "none", padding: 0, textAlign: "left", cursor: "pointer" }}
                >
                  <div style={{ fontSize: 13, fontWeight: 500, color: "var(--ink)" }}>
                    <span style={{ color: "var(--ink-muted)", fontSize: 10, marginRight: 6 }}>{open ? "▾" : "▸"}</span>
                    {t.name}
                  </div>
                  <div style={{ fontSize: 11, color: "var(--ink-muted)", marginTop: 2 }}>{t.category} · {t.language}</div>
                </button>
                <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 99, background: "var(--surface-raised)", color: STATUS_COLOR[t.status] ?? "var(--ink-muted)", border: `1px solid ${STATUS_COLOR[t.status] ?? "var(--line-bold)"}44`, flexShrink: 0 }}>
                  {t.status}
                </span>
                <button onClick={() => { if (confirm(`Excluir template "${t.name}"?`)) deleteMut.mutate(t.name); }} aria-label="Excluir" style={{ background: "transparent", border: "none", color: "var(--brand)", fontSize: 14, cursor: "pointer", padding: "2px 6px", flexShrink: 0 }}><XIcon size={13} /></button>
              </div>
              {open && <TemplateBody components={t.components ?? []} />}
            </div>
            );
          })}
          {!isLoading && templates.length === 0 && (
            <span style={{ fontSize: 13, color: "var(--ink-muted)" }}>Nenhum template cadastrado ainda.</span>
          )}
        </div>

        {/* Formulário */}
        {creating ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 12, borderTop: "1px solid var(--line)", paddingTop: 16, overflowY: "auto" }}>
            <span style={{ fontSize: 13, fontWeight: 500, color: "var(--ink)" }}>Novo template</span>
            <div style={{ display: "flex", gap: 8 }}>
              <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 4 }}>
                <label style={{ fontSize: 11, color: "var(--ink-secondary)" }}>Nome</label>
                <input style={inputStyle} value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, "_") }))} placeholder="ex: boas_vindas" />
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                <label style={{ fontSize: 11, color: "var(--ink-secondary)" }}>Idioma</label>
                <select style={selectStyle} value={form.language} onChange={(e) => setForm((f) => ({ ...f, language: e.target.value }))}>
                  <option value="pt_BR">pt_BR</option>
                  <option value="en_US">en_US</option>
                  <option value="es_AR">es_AR</option>
                </select>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                <label style={{ fontSize: 11, color: "var(--ink-secondary)" }}>Categoria</label>
                <select style={selectStyle} value={form.category} onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}>
                  <option value="UTILITY">Utility</option>
                  <option value="MARKETING">Marketing</option>
                  <option value="AUTHENTICATION">Auth</option>
                </select>
              </div>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <label style={{ fontSize: 11, color: "var(--ink-secondary)" }}>Corpo da mensagem</label>
              <textarea style={{ ...inputStyle, resize: "vertical", minHeight: 80 }} value={form.bodyText} onChange={(e) => setForm((f) => ({ ...f, bodyText: e.target.value }))} placeholder="Texto do corpo. Use {{1}}, {{2}} para variáveis." />
            </div>
            {varNums.length > 0 && (
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                <label style={{ fontSize: 11, color: "var(--ink-secondary)" }}>
                  Exemplos das variáveis <span style={{ color: "var(--ink-muted)" }}>(a Meta usa na aprovação do template)</span>
                </label>
                {varNums.map((n) => (
                  <div key={n} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <code style={{ fontSize: 12, color: "var(--violet)", fontFamily: "var(--font-mono)", flexShrink: 0, width: 42 }}>{`{{${n}}}`}</code>
                    <input
                      style={inputStyle}
                      value={examples[n] ?? ""}
                      onChange={(e) => setExamples((ex) => ({ ...ex, [n]: e.target.value }))}
                      placeholder={n === "1" ? "ex: João" : "ex: Valquíria"}
                    />
                  </div>
                ))}
              </div>
            )}
            {formError && <span style={{ fontSize: 12, color: "var(--danger)" }}>{formError}</span>}
            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={() => createMut.mutate()} disabled={!form.name || !form.bodyText || examplesMissing || createMut.isPending} style={{ background: "var(--brand)", color: "#fff", border: "none", borderRadius: 8, padding: "8px 20px", fontSize: 13, cursor: examplesMissing ? "not-allowed" : "pointer", opacity: examplesMissing ? 0.6 : 1 }}>
                {createMut.isPending ? "Enviando..." : "Enviar para aprovação"}
              </button>
              <button onClick={() => { setCreating(false); setFormError(null); setExamples({}); }} style={{ background: "transparent", color: "var(--ink-secondary)", border: "1px solid var(--line-bold)", borderRadius: 8, padding: "8px 16px", fontSize: 13, cursor: "pointer" }}>Cancelar</button>
            </div>
          </div>
        ) : canCreate ? (
          <button onClick={() => setCreating(true)} style={{ background: "transparent", color: "var(--brand)", border: "1px solid var(--brand-line)", borderRadius: 8, padding: "8px 0", fontSize: 13, cursor: "pointer" }}>
            + Novo template
          </button>
        ) : null}
      </DialogPanel>
    </div>
  );
}
